/**
 * NGAP Logic Engine — Smart dental act auto-detection
 *
 * Given a clinical context (selected teeth, surfaces, tooth types, parts),
 * automatically suggests the most relevant NGAP billing acts from the catalog.
 *
 * Rules derived from the official NGAP nomenclature:
 * - Fillings: surface count → D700/D701/D702
 * - Endodontics: tooth type → D704/D705/D706
 * - Extractions: first/subsequent in session → D713/D714
 * - Prosthetics: tooth count → D761-D772
 * - Prevention: tooth count → D911/D912
 * - Crowns: material-based D748-D756
 */

import type { NGAPAct, TreatmentCategory } from '../types/records';

// ── Selection context from the chart ──
export interface SelectionContext {
  selectedTeeth: number[];                      // FDI numbers of fully selected teeth
  selectedSurfaces: Record<string, string[]>;   // { "16": ["occlusal","buccal"] }
  selectedParts: Record<string, string[]>;      // { "16": ["crown"] }
  toothTypes: Record<string, string>;           // { "16": "molar" }
}

export interface SuggestedAct {
  act: NGAPAct;
  reason: string;       // Human-readable explanation
  confidence: number;   // 0-1 score (1 = exact match)
  category: 'auto' | 'related' | 'contextual';
}

// ── Tooth type classification ──
function getToothCategory(fdi: number): 'incisor' | 'canine' | 'premolar' | 'molar' {
  const pos = fdi % 10;
  if (pos <= 2) return 'incisor';
  if (pos === 3) return 'canine';
  if (pos <= 5) return 'premolar';
  return 'molar';
}

function isUpperTooth(fdi: number): boolean {
  return fdi >= 11 && fdi <= 28;
}

// ── Find act by code from flat list ──
function findAct(acts: NGAPAct[], code: string): NGAPAct | undefined {
  return acts.find(a => a.code === code);
}

// ── Flatten categories into act list ──
export function flattenCatalog(categories: TreatmentCategory[]): NGAPAct[] {
  const all: NGAPAct[] = [];
  for (const cat of categories) {
    if (cat.acts) all.push(...cat.acts);
  }
  return all;
}

// ── Main detection function ──
export function detectSuggestedActs(
  context: SelectionContext,
  categories: TreatmentCategory[],
  sessionExtractions: number = 0, // how many extractions already applied this session
): SuggestedAct[] {
  const allActs = flattenCatalog(categories);
  if (allActs.length === 0) return [];

  const suggestions: SuggestedAct[] = [];
  const addedCodes = new Set<string>();

  function addSuggestion(code: string, reason: string, confidence: number, category: SuggestedAct['category'] = 'auto') {
    if (addedCodes.has(code)) return;
    const act = findAct(allActs, code);
    if (!act) return;
    addedCodes.add(code);
    suggestions.push({ act, reason, confidence, category });
  }

  // ── Surface-based detection ──
  const surfaceEntries = Object.entries(context.selectedSurfaces);
  if (surfaceEntries.length > 0) {
    // Surfaces selected on a single tooth
    const [fdi, surfaces] = surfaceEntries[0];
    const surfaceCount = surfaces.length;
    const toothCat = getToothCategory(parseInt(fdi));

    // Filing detection (surface count)
    if (surfaceCount === 1) {
      addSuggestion('D700', `Obturation 1 surface (${surfaces[0]})`, 0.95);
    } else if (surfaceCount === 2) {
      addSuggestion('D701', `Obturation 2 surfaces (${surfaces.join(', ')})`, 0.95);
    } else if (surfaceCount >= 3) {
      addSuggestion('D702', `Obturation ≥3 surfaces (${surfaces.join(', ')})`, 0.95);
    }

    // Always suggest composite for surface selections
    if (surfaceCount > 0) {
      addSuggestion('D707', 'Restauration avec ancrage radiculaire', 0.3, 'related');
    }

    // Caries on surfaces
    addSuggestion('D712', 'Scellement prophylactique (sealant)', 0.4, 'related');

    // Sealant suggestion for molars/premolars
    if (toothCat === 'molar' || toothCat === 'premolar') {
      addSuggestion('D712', 'Scellement sillon (molaire/prémolaire)', 0.5, 'related');
    }
  }

  // ── Part-based detection ──
  const partEntries = Object.entries(context.selectedParts);
  if (partEntries.length > 0) {
    for (const [fdi, parts] of partEntries) {
      const toothCat = getToothCategory(parseInt(fdi));

      if (parts.includes('root')) {
        // Root selected → endodontics
        if (toothCat === 'molar') {
          addSuggestion('D706', `Pulpectomie molaire (#${fdi})`, 0.9);
        } else if (toothCat === 'premolar') {
          addSuggestion('D705', `Pulpectomie prémolaire (#${fdi})`, 0.9);
        } else {
          addSuggestion('D704', `Pulpectomie incisive/canine (#${fdi})`, 0.9);
        }
        addSuggestion('D703', 'Pulpotomie (chambre pulpaire)', 0.5, 'related');
        addSuggestion('D738', 'Curetage périapical', 0.3, 'related');
      }

      if (parts.includes('crown')) {
        // Crown part selected → crown/veneer/restoration
        addSuggestion('D748', 'Couronne provisoire', 0.7);
        addSuggestion('D900', 'Couronne céramo-métallique', 0.6, 'related');
        addSuggestion('D902', 'Facette céramique (veneer)', 0.4, 'related');
        addSuggestion('D904', 'Dent provisoire résine', 0.4, 'related');
      }

      if (parts.includes('neck')) {
        // Neck selected → periodontal
        addSuggestion('D708', 'Détartrage', 0.85);
        addSuggestion('D742', 'Gingivectomie partielle', 0.4, 'related');
      }
    }
  }

  // ── Whole-tooth selection detection ──
  const toothCount = context.selectedTeeth.length;
  if (toothCount > 0) {
    // Extraction detection (first vs subsequent)
    if (sessionExtractions === 0) {
      addSuggestion('D713', `Extraction 1ère dent permanente`, 0.7);
    } else {
      addSuggestion('D714', `Extraction suivante (séance)`, 0.7);
    }
    addSuggestion('D715', 'Extraction dent lactéale 1ère', 0.3, 'related');

    // Check for wisdom teeth
    const wisdomTeeth = context.selectedTeeth.filter(t => [18, 28, 38, 48].includes(t));
    if (wisdomTeeth.length > 0) {
      addSuggestion('D720', 'Extraction dent de sagesse incluse', 0.8);
      addSuggestion('D725', 'Extraction dent de sagesse semi-incluse', 0.7, 'related');
    }

    // Implant suggestion for missing teeth
    addSuggestion('D903', 'Inlay-core (post & core)', 0.4, 'related');

    // Multi-tooth prosthetics
    if (toothCount >= 1 && toothCount <= 14) {
      // Map count to prosthetic code D761-D772
      const prosthCodes: Record<number, string> = {
        1: 'D761', 2: 'D762', 3: 'D763', 4: 'D764', 5: 'D765',
        6: 'D766', 7: 'D767', 8: 'D768', 9: 'D769', 10: 'D770',
        11: 'D771', 12: 'D772', 13: 'D772', 14: 'D772',
      };
      const code = prosthCodes[toothCount];
      if (code) {
        addSuggestion(code, `Prothèse partielle ${toothCount} dent(s)`, 0.3, 'contextual');
      }
    }

    // Remineralization / prevention (count-based)
    if (toothCount === 1) {
      addSuggestion('D911', 'Reminéralisation 1 dent', 0.4, 'contextual');
    } else if (toothCount >= 2 && toothCount <= 6) {
      addSuggestion('D912', `Reminéralisation ${toothCount} dents (2-6)`, 0.5, 'contextual');
    }

    // Crown for single tooth selection
    if (toothCount === 1) {
      const fdi = context.selectedTeeth[0];
      const toothCat = getToothCategory(fdi);

      addSuggestion('D748', 'Couronne provisoire', 0.5, 'related');
      addSuggestion('D900', 'Couronne céramo-métallique', 0.4, 'related');

      // Endo based on tooth type
      if (toothCat === 'molar') {
        addSuggestion('D706', `Pulpectomie molaire (#${fdi})`, 0.5, 'contextual');
      } else if (toothCat === 'premolar') {
        addSuggestion('D705', `Pulpectomie prémolaire (#${fdi})`, 0.5, 'contextual');
      } else {
        addSuggestion('D704', `Pulpectomie incisive/canine (#${fdi})`, 0.5, 'contextual');
      }
    }
  }

  // ── If nothing specific selected, no suggestions ──
  // Sort by confidence descending
  suggestions.sort((a, b) => b.confidence - a.confidence);

  return suggestions;
}

// ── Tooth type label for UI ──
export function getToothTypeLabel(fdi: number): string {
  const cat = getToothCategory(fdi);
  const jaw = isUpperTooth(fdi) ? 'Upper' : 'Lower';
  const side = (fdi >= 11 && fdi <= 18) || (fdi >= 41 && fdi <= 48) ? 'Left' : 'Right';
  const labels: Record<string, string> = {
    incisor: 'Incisor',
    canine: 'Canine',
    premolar: 'Premolar',
    molar: 'Molar',
  };
  return `${jaw} ${side} ${labels[cat]} (#${fdi})`;
}

// ── Selection summary for display ──
export function getSelectionSummary(context: SelectionContext): string {
  const parts: string[] = [];

  if (context.selectedTeeth.length > 0) {
    parts.push(`${context.selectedTeeth.length} dent${context.selectedTeeth.length > 1 ? 's' : ''}: ${context.selectedTeeth.join(', ')}`);
  }

  const surfEntries = Object.entries(context.selectedSurfaces);
  if (surfEntries.length > 0) {
    for (const [fdi, surfaces] of surfEntries) {
      parts.push(`#${fdi} surfaces: ${surfaces.join(', ')}`);
    }
  }

  const partEntries = Object.entries(context.selectedParts);
  if (partEntries.length > 0) {
    for (const [fdi, p] of partEntries) {
      parts.push(`#${fdi} parties: ${p.join(', ')}`);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : 'Aucune sélection';
}
